
import { Component, Prop, Vue, Watch } from "vue-property-decorator";

import CREATE_USER_CERTIFICATION from "@/apollo/profile/certifications/CreateCertification.gql";
import UPDATE_USER_CERTIFICATION from "@/apollo/profile/certifications/UpdateCertification.gql";

import ModalFullScreen from "@/components/modal/ModalFullScreenNew.vue";
import ModalHeader from "@/components/modal/ModalHeader.vue";
import FormGroup from "@/components/form/FormGroup.vue";
import FormDatepicker from "@/components/form/FormDatepicker.vue";
import FormTextarea from "@/components/form/FormTextarea.vue";
import UIButton from "@/components/button/ui-button.vue";

import { User_Certification } from "@/generated/graphql";
import { NOTIFICATION } from "@/entities/Events";
import Message from '@/data/notificationsMessages';

@Component({
  components: {
    ModalFullScreen,
    ModalHeader,
    FormDatepicker,
    FormTextarea,
    FormGroup,
    UIButton,
  },
  model: {
    prop: "showDialog",
    event: "toggle",
  },
})
export default class EducationModal extends Vue {
  @Prop({ type: Object, default: undefined }) readonly certification?: User_Certification;
  @Prop({ type: String, required: true }) readonly userId!: string;
  @Prop({ type: Boolean, default: false }) readonly showDialog!: boolean;

  id?: string = undefined;
  title: string = "";
  institution: string = "";
  startDate: string | null = null;
  endDate: string | null = null;

  @Watch("certification", { immediate: true })
  watchProfile(certification: User_Certification): void {
    this.id = certification?.id;
    this.title = certification?.title ?? "";
    this.institution = certification?.institution ?? "";
    this.startDate = certification?.start_date ?? null;
    this.endDate = certification?.end_date ?? null;
  }

  get modalTitle(): string {
    const prefix = this.isEditing ? "Edit" : "Add";
    return `${prefix} Certification`;
  }

  get isEditing(): boolean {
    return Boolean(this.certification);
  }

  get certificationSet(): Partial<User_Certification> {
    return {
      user_id: this.userId,
      title: this.title,
      institution: this.institution,
      start_date: this.startDate,
      end_date: this.endDate,
    };
  }

  closeModal(): void {
    this.$emit("toggle", false);
  }

  async submit(): Promise<void> {
    try {
      if (this.isEditing) {
        await this.update();
      } else {
        await this.create();
      }

      const successMessage = this.isEditing
        ? `${this.institution} was successfully updated.`
        : `${this.institution} added to your certification.`;
      this.$eventBus.emit(
        NOTIFICATION,
        Message.successMessage(successMessage)
      );
    } catch (error) {
      this.$eventBus.emit(
        NOTIFICATION,
        Message.errorMessage(error.message)
      );
    } finally {
      this.closeModal();
    }
  }

  async create(): Promise<void> {
    await this.$apollo.mutate({
      mutation: CREATE_USER_CERTIFICATION,
      variables: {
        certificationSet: this.certificationSet,
      },
    });
    this.$emit("added");
  }

  async update(): Promise<void> {
    await this.$apollo.mutate({
      mutation: UPDATE_USER_CERTIFICATION,
      variables: {
        certificationSet: this.certificationSet,
        id: this.id,
      },
    });
    this.$emit("updated");
  }
}
